import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, ArrowRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";
import { useApp } from "@/contexts/AppContext";
import { formatSAR } from "@/lib/i18n";
import BrandLogo from "@/components/BrandLogo";

export default function Invoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang, toggleLang } = useApp();
  const [order, setOrder] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null)).finally(() => setLoading(false));
    api.get(`/bank-info`).then((r) => setBank(r.data)).catch(() => setBank(null));
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  if (!order) {
    return <div className="min-h-screen flex items-center justify-center">Order not found</div>;
  }

  const Arrow = lang === "ar" ? ArrowRight : ArrowLeft;
  const dateStr = new Date(order.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB");

  const qrPayload = JSON.stringify({
    seller: "BeforeTheDeadline",
    vat: "300000000000003",
    timestamp: order.created_at,
    total: order.total,
    vat_amount: order.vat,
    order: order.order_number,
  });

  return (
    <div className="min-h-screen bg-muted/30 py-10 px-4">
      <div className="max-w-3xl mx-auto mb-6 flex items-center justify-between print-hide">
        <Button variant="ghost" onClick={() => navigate("/admin/orders")} data-testid="invoice-back" className="gap-2">
          <Arrow className="h-4 w-4" />
          {t.orders}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={toggleLang} data-testid="invoice-language-switcher">
            {t.language}
          </Button>
          <Button onClick={() => window.print()} className="gap-2" data-testid="invoice-print">
            <Printer className="h-4 w-4" />
            {t.print}
          </Button>
        </div>
      </div>

      <div
        className="invoice-sheet max-w-3xl mx-auto bg-white text-gray-900 rounded-xl border border-border shadow-ambient overflow-hidden"
        data-testid="invoice-sheet"
      >
        <div className="bg-primary text-primary-foreground p-8 flex justify-between items-start gap-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <BrandLogo size={44} />
              <span className="text-xl font-bold">{t.brand}</span>
            </div>
            <p className="text-sm text-white/80">{t.storeAddress}</p>
            <p className="text-xs text-white/70 mt-1">{t.storeVat}</p>
          </div>
          <div className="text-end">
            <p className="text-xs uppercase tracking-widest text-white/70">{t.invoice}</p>
            <p className="text-2xl font-bold mt-1 font-mono" data-testid="invoice-number">{order.order_number}</p>
            <p className="text-xs text-white/70 mt-2">{dateStr}</p>
            <p className="mt-3 inline-block bg-[#FFD60A] text-black border border-black/10 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest">
              {order.payment_status === "paid" ? t.paid : t.pending}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-8 border-b border-gray-200">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{t.billedTo}</p>
            <p className="font-semibold">{order.customer_name}</p>
            <p className="text-sm text-gray-600 mt-1">{order.customer_mobile}</p>
            <p className="text-sm text-gray-600">{order.customer_email}</p>
            <p className="text-sm text-gray-600">{order.customer_city}</p>
          </div>
          <div className="sm:text-end">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{t.channel}</p>
            <p className="font-semibold capitalize">{order.channel}</p>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-3 mb-2">{t.status}</p>
            <p className="font-semibold capitalize">{order.status}</p>
          </div>
        </div>

        <div className="p-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-start py-2 text-xs uppercase tracking-widest text-gray-500 font-bold">{t.item}</th>
                <th className="text-center py-2 text-xs uppercase tracking-widest text-gray-500 font-bold">{t.qty}</th>
                <th className="text-end py-2 text-xs uppercase tracking-widest text-gray-500 font-bold">{t.price}</th>
                <th className="text-end py-2 text-xs uppercase tracking-widest text-gray-500 font-bold">{t.lineTotal}</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it, i) => (
                <tr key={`${it.product_id || "x"}-${it.variant_id || ""}-${it.service_id || ""}-${i}`} className="border-b border-gray-100" data-testid={`invoice-item-${i}`}>
                  <td className="py-3">
                    <div className="font-medium">{lang === "ar" ? it.name_ar : it.name_en}</div>
                    <div className="text-xs text-gray-500 space-y-0.5 mt-0.5">
                      {(it.variant_name_en || it.variant_name_ar) && (
                        <div>{t.style}: {lang === "ar" ? it.variant_name_ar : it.variant_name_en}</div>
                      )}
                      {(it.service_name_en || it.service_name_ar) && (
                        <div>{t.serviceType}: {lang === "ar" ? it.service_name_ar : it.service_name_en}</div>
                      )}
                    </div>
                  </td>
                  <td className="text-center py-3">{it.qty}</td>
                  <td className="text-end py-3">{formatSAR(it.price, lang)}</td>
                  <td className="text-end py-3 font-semibold">{formatSAR(it.line_total, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-8 pb-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{t.scanToVerify}</p>
              <div className="bg-white p-2 inline-block border border-gray-200 rounded-md">
                <QRCodeSVG value={qrPayload} size={110} bgColor="#ffffff" fgColor="#000000" level="M" />
              </div>
            </div>
            <div className="bg-white overflow-hidden">
              <Barcode
                value={order.order_number}
                height={50}
                fontSize={12}
                background="#ffffff"
                lineColor="#000000"
                displayValue
              />
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <Row label={t.subtotal} value={formatSAR(order.subtotal, lang)} />
            <Row label={t.vat} value={formatSAR(order.vat, lang)} />
            <div className="flex justify-between text-lg font-bold border-t-2 border-gray-300 pt-3 mt-2">
              <span>{t.total}</span>
              <span data-testid="invoice-total">{formatSAR(order.total, lang)}</span>
            </div>
            <div className="text-xs text-gray-500 pt-3 leading-relaxed">
              {lang === "ar"
                ? "شكراً لاستخدامك BeforeTheDeadline. يتم تنزيل قالبك تلقائياً بعد تأكيد الدفع."
                : "Thank you for choosing BeforeTheDeadline. Your template downloads automatically after payment is confirmed."}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-8 py-4 text-xs text-gray-600 space-y-2">
          {bank && bank.iban && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="uppercase tracking-widest text-[10px] font-bold text-gray-500">
                {lang === "ar" ? "تحويل بنكي" : "Bank transfer"}:
              </span>
              <span>{bank.name}</span>
              <span className="font-mono">{bank.iban}</span>
              <span className="font-mono">SWIFT {bank.swift}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span>{t.brand} · beforethedeadline.com</span>
            <span>VAT 300000000000003</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
